import { Actions, Async, Dispatchers } from '@client/new/helpers/types';
import { getDispatchers } from '@client/new/helpers/types-utils';
import { trackSessionEvent } from '@services/session-tracker/sessionTracker';
import { useEffect, useState } from 'react';
import { ExtrasModel } from '../model/extras.model';
import { ContentSlides, ContentTree } from '../model/extras.domain';
import {
  defaultValues,
  toMixpanelContentType,
  toUpdateContentInput,
  updateTreeItems,
} from '../viewModel/extras.mappers';
import { v4 } from 'uuid';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  toErrors,
  ErrorsVM,
} from '@client/new/pages/promotions/view-model/newDiscount.mappers';
import toast from 'react-hot-toast';
import { showExtrasCreatedToast } from '../services/showExtrasCreatedToast';
import { CreateImageInput, CreateSlidesInput } from 'generated/graphql';
import { updateSlidesInputSchema } from './slides.schema';
import { validateImage } from '../viewModel/extras.validation';
import { trackEvent } from '@services/tracking';
import { useKeepScrollPosition } from '@client/new/hooks/useKeepScrollPosition';
import {
  createFormRegister,
  createOnChangeHandler,
  ExtendedFormRegister,
  getFormMode,
} from '../viewModel/formHelpers';

// Slide count constraints
const MAX_SLIDES = 30;
const MIN_SLIDES = 1;

const limitFilesByCapacity = async (
  setState: React.Dispatch<React.SetStateAction<SlidesState>>,
  incoming: File[],
): Promise<File[]> => {
  if (!incoming || incoming.length === 0) return [];
  let currentChildrenCount = 0;
  let currentUploadsCount = 0;
  setState((prev) => {
    currentChildrenCount = prev.children.length;
    currentUploadsCount = prev.uploads.length;
    return prev;
  });
  const existingTotal = currentChildrenCount + currentUploadsCount;
  if (existingTotal >= MAX_SLIDES) {
    toast.error(`Maximum of ${MAX_SLIDES} slides reached`);
    return [];
  }
  const available = MAX_SLIDES - existingTotal;
  if (incoming.length > available) {
    toast.error(
      `You can only add ${available} more slide${available === 1 ? '' : 's'}. ${
        incoming.length - available
      } file${incoming.length - available === 1 ? '' : 's'} ignored.`,
    );
    return incoming.slice(0, available);
  }

  const files = await Promise.all(
    incoming.map(async (file) => {
      const validation = await validateImage(file, 1125, 2436);
      if (!validation.isValid) {
        toast.error(`Invalid image ${file.name}. ${validation.error}`);
        return null;
      }
      return file;
    }),
  );

  return files.filter((f): f is File => !!f);
};

type SlidesActionMap = {
  PrepareCreate: { parentId: string };
  Edit: ContentSlides['value'];
  Create: File[];
  Cancel: null;
  PrepareReupload: null;
  Reupload: File[];
  ReorderSlides: string[];
  RetryFile: string;
  RemoveUpload: string;
  RemoveSlide: string;
};

export type SlidesVMAction = Actions<SlidesActionMap>;

export type SlideUpload = {
  status: 'pending' | 'uploading' | 'error' | 'done';
  id: string;
  file: File;
  progress: number;
};

type SlidesMode = 'none' | 'create' | 'edit' | 'reupload';

export type SlidesState = {
  parentId: string;
  mode: SlidesMode;
  content: ContentSlides['value'];
  uploads: SlideUpload[];
  children: ContentTree[];
};

type SlidesViewModelParams = {
  model: ExtrasModel;
  creatorId: number;
};

export type SlidesViewModel = SlidesState &
  Dispatchers<SlidesActionMap> & {
    register: ExtendedFormRegister<ContentSlides['value']>;
    errors: ErrorsVM;
    values: ContentSlides['value'];
    onChange: <K extends keyof ContentSlides['value']>(
      name: K,
      value: ContentSlides['value'][K],
    ) => void;
  };

export const useSlidesViewModel = (
  p: SlidesViewModelParams,
): Async<SlidesViewModel> => {
  const [showErrors, setShowErrors] = useState<boolean>(false);

  const [state, setState] = useState<SlidesState>({
    parentId: '',
    mode: 'none',
    content: { ...defaultValues['ContentSlides'] },
    uploads: [],
    children: [],
  });

  useKeepScrollPosition(state.mode, 'none');

  const form = useForm({
    reValidateMode: 'onBlur',
    defaultValues: defaultValues['ContentSlides'],
    resolver: zodResolver(updateSlidesInputSchema),
  });

  useEffect(() => {
    if (state.content && Object.keys(state.content).length > 0)
      form.reset({ ...state.content }); // TODO
  }, [form, state.content, state.parentId]);

  useEffect(() => {
    if (p.model.data.type !== 'fetched') return;
    if (!state.content.id) return;
    const children = p.model.data.value.childrenMap[state.content.id] || [];

    setState((prev) => ({ ...prev, children }));
  }, [p.model.data, state.content.id]);

  useEffect(() => {
    if (state.content.id === '') return;
    state.uploads.forEach((upload) => {
      if (upload.status === 'pending') {
        setState((prev) => ({
          ...prev,
          uploads: prev.uploads.map((u) =>
            u.id === upload.id ? { ...u, status: 'uploading' } : u,
          ),
        }));

        const input: CreateImageInput = {
          id: upload.id,
          title: upload.file.name,
          parentId: state.content.id,
          isPublished: true,
        };

        uploadSlide({
          file: upload.file,
          input,
          model: p.model,
          onProgress: (progress) => {
            setState((prev) => ({
              ...prev,
              uploads: prev.uploads.map((u) =>
                u.id === upload.id ? { ...u, progress } : u,
              ),
            }));
          },
        })
          .then(() => {
            setState((prev) => ({
              ...prev,
              uploads: prev.uploads.map((u) =>
                u.id === upload.id
                  ? { ...u, progress: 100, status: 'done' }
                  : u,
              ),
            }));
          })
          .catch((error) => {
            console.error('Error uploading slide:', error);
            setState((prev) => ({
              ...prev,
              uploads: prev.uploads.map((u) =>
                u.id === upload.id ? { ...u, status: 'error' } : u,
              ),
            }));
            toast.error(`Failed to upload slide: ${upload.file.name}`);
          });
      }
    });
  }, [state.uploads, state.content.id, p.model]);

  useEffect(() => {
    if (state.uploads.length === 0) return;
    if (state.uploads.every((u) => u.status === 'done')) {
      reorderSlides({
        newOrder: Array.from(
          new Set([
            ...state.children.map((c) => c.id),
            ...state.uploads.map((u) => u.id),
          ]),
        ),
        model: p.model,
        setState,
      });

      setState((prev) => ({
        ...prev,
        mode: 'edit',
        uploads: [],
      }));
    }
  }, [state.uploads, state.children, p.model]);

  const values = form.watch();

  const onSubmit = (value: ContentSlides['value']) => {
    p.model
      .update(
        state.content.id,
        toUpdateContentInput({ type: 'ContentSlides', value }),
      )
      .then(() => {
        setState((prev) => ({
          ...prev,
          content: {
            ...prev.content,
            ...value,
          },
        }));

        trackEvent({
          type: 'Extras Block Saved',
          payload: {
            title: value.title,
            type: toMixpanelContentType('ContentSlides'),
            'video time': 0,
            'video aspect ratio': 'n/a',
            'Comments on': false,
          },
        });
      })
      .catch((error) => {
        console.error('Error updating Slides:', error);
        toast.error('Failed to update Slides content');
      });
  };

  const onError = () => {
    setShowErrors(true);
  };

  const register = createFormRegister({
    form,
    onSubmit,
    onError,
    showErrors,
    mode: getFormMode(state.mode),
  });

  const dispatchers = getDispatchers<SlidesActionMap>(
    _dispatch(setState, p.model, p.creatorId),
  );

  const onChange = createOnChangeHandler({
    form,
    onSubmit,
    onError,
    mode: getFormMode(state.mode),
  });

  return {
    type: 'fetched',
    value: {
      ...state,
      ...dispatchers,
      register,
      onChange,
      values,
      errors: showErrors ? toErrors(form.formState.errors) : null,
    },
  };
};

const _dispatch =
  (
    setState: React.Dispatch<React.SetStateAction<SlidesState>>,
    model: ExtrasModel,
    creatorId: number,
  ) =>
  (a: SlidesVMAction) => {
    trackSessionEvent('ViewAction', a);
    switch (a.type) {
      case 'PrepareCreate':
        return setState((prev) => ({
          ...prev,
          parentId: a.payload.parentId,
          mode: 'create',
          uploads: [],
          content: { ...defaultValues['ContentSlides'] },
        }));

      case 'Edit':
        return setState((prev) => ({
          ...prev,
          content: a.payload,
          mode: 'edit',
        }));

      case 'Create':
        return onCreate(setState, model, a.payload, creatorId);

      case 'PrepareReupload':
        return setState((prev) => ({
          ...prev,
          mode: 'reupload',
          uploads: [],
        }));

      case 'Reupload':
        return onReupload(setState, model, a.payload);

      case 'Cancel':
        return setState(onCancel);

      case 'RetryFile':
        return setState((prev) => {
          const upload = prev.uploads.find((u) => u.id === a.payload);
          if (!upload) return prev;

          const updatedUploads = prev.uploads.map((u) =>
            u.id === a.payload
              ? { ...u, status: 'pending' as const, progress: 0 }
              : u,
          );

          return {
            ...prev,
            uploads: updatedUploads,
          };
        });

      case 'RemoveUpload':
        return setState((prev) => {
          const updatedUploads = prev.uploads.filter((u) => u.id !== a.payload);
          const remainingTotal = prev.children.length + updatedUploads.length;
          if (remainingTotal < MIN_SLIDES) {
            toast.error(`At least ${MIN_SLIDES} slide required`);
            return prev;
          }
          return {
            ...prev,
            uploads: updatedUploads,
          };
        });

      case 'RemoveSlide':
        return removeSlide({ slideId: a.payload, model, setState });

      case 'ReorderSlides':
        return reorderSlides({ newOrder: a.payload, model, setState });
    }
  };

export const onCreate = (
  setState: React.Dispatch<React.SetStateAction<SlidesState>>,
  model: ExtrasModel,
  files: File[],
  creatorId: number,
) => {
  limitFilesByCapacity(setState, files).then((files) => {
    if (files.length === 0) return;

    const slideId = v4();
    const input: CreateSlidesInput = {
      parentId: (() => {
        let parent = '';
        setState((prev) => {
          parent = prev.parentId;
          return prev; // no change yet
        });
        return parent;
      })(),
      id: slideId,
      title: 'My Slides',
      downloadable: false,
      isPublished: false,
      coverURL: '',
    };

    model
      .create({
        input: { type: 'ContentSlides', value: input },
      })
      .then(() => {
        setState((prev) => ({
          ...prev,
          content: {
            ...prev.content,
            id: slideId,
            title: '',
          },
        }));
        trackEvent({
          type: 'Guides Upload',
        });
        showExtrasCreatedToast(creatorId);
      })
      .catch((error) => {
        console.error('Error creating Slides:', error);
      });

    buildUploads(files).then((uploads) => {
      if (uploads.length === 0) return;
      setState((prev) => ({
        ...prev,
        uploads,
      }));
    });
  });
};

const onCancel = (prev: SlidesState): SlidesState => {
  if (
    prev.uploads.some((u) => u.status === 'uploading' || u.status === 'pending')
  )
    return prev;
  const mode = prev.mode === 'reupload' ? 'edit' : 'none';
  return { ...prev, mode, uploads: [] };
};

export const onReupload = (
  setState: React.Dispatch<React.SetStateAction<SlidesState>>,
  model: ExtrasModel,
  files: File[],
) => {
  limitFilesByCapacity(setState, files).then((files) => {
    if (files.length === 0) return;

    buildUploads(files).then((uploads) => {
      if (uploads.length === 0) return;
      setState((prev) => ({
        ...prev,
        uploads,
      }));
    });
  });
};

export const buildUploads = async (files: File[]): Promise<SlideUpload[]> => {
  const results = await Promise.all(
    files.map(async (file) => {
      return {
        id: v4(),
        file,
        progress: 0,
        status: 'pending' as const,
      } as SlideUpload;
    }),
  );
  return results.filter((u): u is SlideUpload => !!u);
};

export const uploadSlide = (p: {
  file: File;
  input: CreateImageInput;
  onProgress: (progress: number) => void;
  model: ExtrasModel;
}) => {
  return p.model.create({
    input: { type: 'ContentImage', value: p.input },
    file: p.file,
    onProgress: p.onProgress,
  });
};

export const removeSlide = (p: {
  slideId: string;
  model: ExtrasModel;
  setState: React.Dispatch<React.SetStateAction<SlidesState>>;
}) => {
  p.setState((prev) => {
    if (prev.children.length <= MIN_SLIDES) {
      toast.error(`At least ${MIN_SLIDES} slide required`);
      return prev;
    }

    const originalChildren = [...prev.children];
    const updatedChildren = prev.children.filter((c) => c.id !== p.slideId);

    const remainingTotal = updatedChildren.length + prev.uploads.length;
    if (remainingTotal < MIN_SLIDES) {
      toast.error(`At least ${MIN_SLIDES} slide required`);
      return prev;
    }

    p.model
      .delete({
        value: {
          ...defaultValues['ContentSlides'], // weird, but required.
          id: p.slideId,
        },
        type: 'ContentSlides',
      })
      .catch((error) => {
        p.setState((prev2) => ({ ...prev2, children: originalChildren }));
        console.error('Error deleting slide:', error);
        toast.error('Failed to delete slide');
      });
    return { ...prev, children: updatedChildren };
  });
};

export const reorderSlides = (p: {
  newOrder: string[];
  model: ExtrasModel;
  setState: React.Dispatch<React.SetStateAction<SlidesState>>;
}) => {
  p.setState((prev) => {
    if (p.model.data.type !== 'fetched') return prev;
    const originalChildren = [...prev.children];
    const updatedChildren: ContentTree[] = p.newOrder.map((id) => ({ id }));

    const tree = updateTreeItems(
      p.model.data.value.contentIds,
      prev.content.id,
      updatedChildren,
    );

    p.model.reorder(tree).catch(() => {
      p.setState((prev) => ({ ...prev, children: originalChildren }));
      toast.error('Failed to reorder slides');
    });
    return { ...prev, children: updatedChildren };
  });
};
